"use client";
import React, { useState, useEffect } from "react";
import DefaultImage from "@/mocks/assets/defaultImage.png";
import Image from "next/image";
import { addToCart } from "@/utils/cartUtils";
import { Item } from "@/dto/item";
import { v4 as uuidv4 } from "uuid";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { DropdownArrowIcon } from "@/components/sortDropdown/dropdownArrowIcon";
import { PrimaryButton } from "@/components/button/primaryButton";
import { AgeRestricted } from "@/components/ageRestricted/ageRestricted";
import { usePathname } from "next/navigation";
import { QuantityStepper } from "@/components/quantityStepper/quantityStepper";
import { Modifier } from "@/dto/modifier";
import { ModifierGroup } from "@/dto/modifierGroup";
import { isEmpty } from "lodash";
import { ModifierRuleBadge } from "@/components/modifierRuleBadge/modifierRuleBadge";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";

export const ItemPage: React.FC<any> = ({
  itemData,
  checkoutEnabled,
  isOpen,
  merchantColor,
  merchant,
}: any) => {
  const t = useTranslations("itemPage");
  const [selectedOptions, setSelectedOptions] = useState<any>({});
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [selectedModifiers, setSelectedModifiers] = useState<any>({});
  const [itemVariantUnavailable, setItemVariantUnavailable] =
    useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(1);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const pathName = usePathname();
  const isShopPage: boolean = pathName?.includes("shop") || false;
  const [isAddtocartClicked, setIsAddtocartClicked] = useState<boolean>(false);

  useEffect(() => {
    setIsPreview(window.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    if (itemData?.items && itemData.items.length > 0) {
      const defaultItem = itemData.items[0];
      setSelectedItem(defaultItem);

      if (defaultItem.options) {
        const defaultOptions = defaultItem.options.reduce(
          (acc: any, option: any) => {
            const variant = itemData.variants.find((v: any) =>
              v.options.some((o: any) => o.id === option.id)
            );
            if (variant) {
              acc[variant.id] = option.id;
            }
            return acc;
          },
          {}
        );
        setSelectedOptions(defaultOptions);
      }
    }
  }, [itemData?.items, itemData?.variants]);

  useEffect(() => {
    if (itemData?.items) {
      const matchedItem = itemData.items.find((item: any) => {
        return item.options?.every((option: any) => {
          return Object.values(selectedOptions).includes(option.id);
        });
      });

      if (matchedItem) {
        setSelectedItem(matchedItem);
        setItemVariantUnavailable(false);
      } else {
        setSelectedItem(null);
        setItemVariantUnavailable(true);
      }
    }
  }, [selectedOptions, itemData?.items]);

  const selectableModifierGroups = Object.values(
    itemData?.modifierGroups || {}
  ).filter(
    (group: any) =>
      selectedItem?.modifierGroupIds.includes(group.id) &&
      group.modifiers?.length > 0 &&
      group.modifiers?.some((modifier: Modifier) => modifier.available)
  );

  useEffect(() => {
    if (selectedItem) {
      const basePrice = selectedItem.price;
      let modifiersTotal = 0;

      selectableModifierGroups.forEach((group: any) => {
        const groupSelections = selectedModifiers[group.id];
        if (groupSelections) {
          Object.keys(groupSelections).forEach((modifierId) => {
            const modifier = group.modifiers.find(
              (m: Modifier) => m.id === modifierId
            );
            if (modifier) {
              modifiersTotal += modifier.price * groupSelections[modifierId];
            }
          });
        }
      });
      const updatedTotalPrice = (basePrice + modifiersTotal) * quantity;
      setTotalPrice(updatedTotalPrice);
    }
  }, [selectedModifiers, selectedItem, quantity, selectableModifierGroups]);

  const handleOptionChange = (variantId: string, optionId: string) => {
    handleQuantityChange(1);
    setSelectedOptions({
      ...selectedOptions,
      [variantId]: optionId,
    });
  };

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup = itemData?.modifierGroups?.[groupId];
    if (!group) return;

    setSelectedModifiers((prev: any) => {
      const newSelectedModifiers = { ...prev };
      const groupSelections: { [key: string]: number } = {
        ...(newSelectedModifiers[groupId] || {}),
      };

      if (isSingleSelection) {
        newSelectedModifiers[groupId] = { [modifierId]: 1 };
        return newSelectedModifiers;
      }

      const isSelected: boolean = groupSelections[modifierId] !== undefined;

      if (isSelected) {
        delete groupSelections[modifierId];
      } else {
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        if (totalGroupQuantity < group.maxAllowed) {
          groupSelections[modifierId] = 1;
        }
      }
      newSelectedModifiers[groupId] = groupSelections;
      return newSelectedModifiers;
    });
  };

  const handleModifierQuantityChange = (
    groupId: string,
    modifierId: string,
    newQuantity: number
  ) => {
    setIsAddtocartClicked(false);
    const group: ModifierGroup = itemData?.modifierGroups?.[groupId];
    if (!group) return;

    if (newQuantity === 0) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = { ...prev };
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          delete newSelectedModifiers[groupId][modifierId];
        }
        return newSelectedModifiers;
      });
      return;
    }

    const groupSelections = selectedModifiers[groupId] || {};
    const otherModifiersQuantity: number = Object.keys(groupSelections).reduce(
      (acc, mId) => {
        if (mId !== modifierId) {
          return acc + groupSelections[mId];
        }
        return acc;
      },
      0
    );

    const availableQuantity: number = Math.min(
      group.maxAllowed - otherModifiersQuantity,
      99
    );

    if (newQuantity >= 1 && newQuantity <= availableQuantity) {
      setSelectedModifiers((prev: any) => {
        const newSelectedModifiers = JSON.parse(JSON.stringify(prev));
        if (newSelectedModifiers[groupId]?.[modifierId]) {
          newSelectedModifiers[groupId][modifierId] = newQuantity;
        }
        return newSelectedModifiers;
      });
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= Number.MAX_SAFE_INTEGER) {
      setQuantity(newQuantity);
    }
  };

  const validateMinimumSelections = () => {
    return selectableModifierGroups.every((group: any) => {
      const groupSelections: { [key: string]: number } =
        selectedModifiers[group.id] || {};
      const totalGroupQuantity: number = Object.values(groupSelections).reduce(
        (acc: number, quantity: number) => acc + quantity,
        0
      );
      return (
        totalGroupQuantity >= group.minRequired &&
        totalGroupQuantity <= group.maxAllowed
      );
    });
  };

  const handleAddToCart = () => {
    if (!validateMinimumSelections() || !selectedItem) {
      setIsAddtocartClicked(true);
      return;
    }

    let finalSelectedModifiers: Modifier[] = [];
    if (itemData?.modifierGroups && !isEmpty(itemData.modifierGroups)) {
      finalSelectedModifiers = Object.keys(selectedModifiers).flatMap(
        (groupId) => {
          const group: ModifierGroup = itemData.modifierGroups[groupId];
          const selections = selectedModifiers[groupId];
          return Object.keys(selections).map((modifierId) => {
            const modifier: Modifier | undefined = group?.modifiers?.find(
              (m: Modifier) => m.id === modifierId
            );
            return {
              name: modifier?.name,
              id: modifier?.id,
              price: modifier?.price,
              groupId: group.id,
              quantity: selections[modifierId],
            };
          });
        }
      );
    }

    const newItem = {
      id: selectedItem.id,
      name: selectedItem.name,
      price: selectedItem.price,
      quantity,
      modifiers: finalSelectedModifiers,
      specialInstructions,
      subtotal: totalPrice,
      total: totalPrice,
      cartId: uuidv4(),
      isAgeRestricted: selectedItem.isAgeRestricted,
      setIsAddtocartClicked,
    };

    addToCart(newItem);
    setQuantity(1);
    setSpecialInstructions("");
    setSelectedModifiers({});
    window.dispatchEvent(new StorageEvent("storage", { key: "cart" }));
  };

  const productImage =
    selectedItem?.imageFilename ||
    (selectedItem?.images && selectedItem.images.length > 0
      ? selectedItem.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, selectedItem.images[0])
      : null);

  let disableAddButton: boolean =
    !selectedItem || selectedItem?.available === false || !isOpen;

  const isAgeRestricted: boolean =
    selectedItem?.isAgeRestricted || itemData?.isAgeRestricted;

  return (
    <div className="flex flex-col items-center pt-6 w-full">
      <div className="flex flex-col sm:flex-row justify-center sm:gap-20 px-8 w-full">
        <div className="flex-shrink-0 w-full sm:w-[420px]">
          <div
            className="relative overflow-hidden rounded-md bg-gray-200 mb-8"
            style={{ paddingBottom: "133.33%" }}
          >
            <Image
              width="0"
              height="0"
              sizes="100vw"
              alt={selectedItem?.name || itemData.name}
              src={productImage ? productImage.source : DefaultImage}
              className="absolute inset-0 w-full h-full object-cover"
              priority
            />
          </div>
        </div>
        <div className="sm:w-[40%]">
          <h1 className="text-4xl font-bold mb-4 break-words">
            {selectedItem?.name || itemData?.name || t("unnamedItem")}
          </h1>
          <p className="text-[28px] font-semibold mb-4">
            {selectedItem ? (
              <>
                <span
                  className={`${!selectedItem.available ? "line-through" : ""}`}
                >
                  ${(selectedItem.price / 100).toFixed(2)}
                </span>
                {!selectedItem.available && (
                  <span className="ml-4">{t("unavailable")}</span>
                )}
              </>
            ) : (
              t("priceNotAvailable")
            )}
          </p>
          {isAgeRestricted && (
            <AgeRestricted
              text={t("ageRestriction")}
              textClassName="text-[16px]"
            />
          )}
          <p className="text-[#637381] font-normal text-base mb-4 break-words whitespace-pre-wrap">
            {selectedItem?.description ||
              itemData?.description ||
              t("noDescription")}
          </p>
          {itemData.variants?.map((variant: any) => (
            <div key={variant.id} className="mb-4">
              <label className="block font-medium text-base mb-2">
                {variant.name}
              </label>
              <Popover className="relative w-[242px]">
                <PopoverButton className="flex items-center gap-x-2 w-full leading-6 text-gray-900 hover:bg-gray-50">
                  <div
                    id="item_data_variants"
                    className="w-full h-[48px] flex flex-row justify-between items-center px-5 py-3 border border-[#1F2A37] rounded-lg"
                  >
                    <p>
                      {variant.options.find(
                        (option: any) =>
                          option.id === selectedOptions[variant.id]
                      )?.name || `${t("select")} ${variant.name}`}
                    </p>
                    <DropdownArrowIcon />
                  </div>
                </PopoverButton>
                <PopoverPanel
                  transition
                  className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[560px] overflow-y-auto"
                >
                  <div className="w-full">
                    {variant.options.map((option: any) => (
                      <CloseButton
                        key={option.id}
                        className="group w-full relative flex flex-row items-start gap-x-6 rounded-lg px-4 py-4 text-sm leading-6 hover:bg-gray-50 cursor-pointer"
                        onClick={() =>
                          handleOptionChange(variant.id, option.id)
                        }
                      >
                        <p className=""> {option.name}</p>
                      </CloseButton>
                    ))}
                  </div>
                </PopoverPanel>
              </Popover>
            </div>
          ))}
          {selectableModifierGroups.length > 0 && (
            <ModifierGroupDisplay
              {...{
                selectableModifierGroups,
                selectedItem,
                selectedModifiers,
                handleModifierChange,
                handleModifierQuantityChange,
                merchantColor,
                isAddtocartClicked,
              }}
            />
          )}
          <div className="mb-4 flex flex-col items-start">
            <label
              htmlFor="input-quantity"
              className="font-medium text-base pb-4"
            >
              {t("quantity")}
            </label>
            <QuantityStepper
              handleQuantityChange={handleQuantityChange}
              quantity={quantity}
              min={1}
              adaName={itemData?.name}
            />
          </div>
          <div className="mb-4">
            <label className="block text-lg font-semibold mb-4">
              {t("specialInstructions")}
            </label>
            <textarea
              className="w-full p-2 border rounded-md max-h-[500px]"
              value={specialInstructions}
              maxLength={200}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder={t("provideInfo")}
            />
          </div>
          {checkoutEnabled && !isPreview && (
            <PrimaryButton
              style={{
                backgroundColor: disableAddButton ? "#C7C7C7" : merchantColor,
              }}
              onClick={handleAddToCart}
              className="w-full text-white disabled:text-black py-2 rounded disabled:bg-[#E5E5E5]"
              disabled={disableAddButton}
            >
              {selectedItem?.available === false ||
              (!isOpen &&
                isShopPage &&
                merchant.isService &&
                merchant.isHybrid) ||
              itemVariantUnavailable
                ? t("itemUnavailable")
                : t("addToCart")}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="w-[96%] my-4 pb-4 border-b border-b-[#D1D5DB]/50"></div>
    </div>
  );
};

const ModifierGroupDisplay = ({
  selectableModifierGroups,
  selectedItem,
  selectedModifiers,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  isAddtocartClicked,
}: any) => {
  useEffect(() => {
    if (isAddtocartClicked) {
      const firstUnmetGroup: any = selectableModifierGroups.find(
        (group: any) => {
          const groupSelections: { [key: string]: number } =
            selectedModifiers[group.id] || {};
          const totalGroupQuantity = Object.values(groupSelections).reduce(
            (acc: number, quantity: number) => acc + quantity,
            0
          );
          return !(
            totalGroupQuantity >= group.minRequired &&
            totalGroupQuantity <= group.maxAllowed
          );
        }
      );

      if (firstUnmetGroup) {
        const element = document.getElementById(firstUnmetGroup.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }
  }, [
    isAddtocartClicked,
    selectableModifierGroups,
    selectedItem,
    selectedModifiers,
  ]);

  return (
    <>
      {selectableModifierGroups.map((group: any) => {
        const groupSelections: { [key: string]: number } =
          selectedModifiers[group.id] || {};
        const totalGroupQuantity: number = Object.values(
          groupSelections
        ).reduce((acc: number, quantity: number) => acc + quantity, 0);
        const isRequirementMet: boolean =
          totalGroupQuantity >= group.minRequired &&
          totalGroupQuantity <= group.maxAllowed;

        return (
          <div key={group.id} id={group.id} className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block font-medium text-base">
                {group.name}
              </label>
              <ModifierRuleBadge
                min={group.minRequired}
                max={group.maxAllowed}
                isRequirementMet={isRequirementMet}
                isAddtocartClicked={isAddtocartClicked}
                modifierGroupId={group.id}
              />
            </div>
            <div>
              <ModifierSelection
                {...{
                  group,
                  selectedModifiers,
                  handleModifierChange,
                  handleModifierQuantityChange,
                  merchantColor,
                  groupSelections,
                  totalGroupQuantity,
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

const ModifierSelection = ({
  group,
  handleModifierChange,
  handleModifierQuantityChange,
  merchantColor,
  groupSelections,
  totalGroupQuantity,
}: any) => {
  const isMaxReached: boolean = totalGroupQuantity >= group.maxAllowed;

  return (
    <>
      {group.modifiers.map((modifier: any) => {
        const isSingleSelection: boolean =
          group.minRequired === 1 && group.maxAllowed === 1;
        const isSelected: boolean = !!groupSelections[modifier.id];
        const isDisabled: boolean =
          (!isSingleSelection && isMaxReached && !isSelected) ||
          !modifier.available;
        const modifierQuantity: number = groupSelections[modifier.id] || 1;
        const disablePlus: boolean =
          modifierQuantity >= Math.min(group.maxAllowed, 99) ||
          totalGroupQuantity >= group.maxAllowed;

        return (
          <div
            key={modifier.id}
            className={`mb-2 ${
              isDisabled ? "text-grayDisabled cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isSingleSelection ? (
                  <input
                    type="radio"
                    name={group.id}
                    value={modifier.id}
                    checked={isSelected && !isDisabled}
                    disabled={isDisabled}
                    onChange={() =>
                      handleModifierChange(
                        group.id,
                        modifier.id,
                        isSingleSelection
                      )
                    }
                    className="mr-2"
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      accentColor: merchantColor,
                      flexShrink: 0,
                    }}
                    data-testid={
                      applyDataTestId(pom.itemPage.modifierInput) + modifier.id
                    }
                  />
                ) : (
                  <input
                    type="checkbox"
                    name={group.id}
                    value={modifier.id}
                    checked={isSelected && !isDisabled}
                    onChange={() =>
                      handleModifierChange(
                        group.id,
                        modifier.id,
                        isSingleSelection
                      )
                    }
                    disabled={isDisabled}
                    className="mr-2"
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      accentColor: merchantColor,
                      flexShrink: 0,
                    }}
                    data-testid={
                      applyDataTestId(pom.itemPage.modifierInput) + modifier.id
                    }
                  />
                )}
                <div
                  className={`${
                    isDisabled
                      ? "text-grayDisabled cursor-not-allowed"
                      : "text-black cursor-pointer"
                  }`}
                  onClick={() =>
                    !isDisabled &&
                    handleModifierChange(
                      group.id,
                      modifier.id,
                      isSingleSelection
                    )
                  }
                >
                  <span>{modifier.name}</span>
                </div>
              </div>
              <div>
                <span
                  data-testid={
                    applyDataTestId(pom.itemPage.modifierPrice) + modifier.id
                  }
                  className={`text-right font-normal text-[15px] ${
                    isDisabled
                      ? "text-grayDisabled"
                      : isSelected
                      ? "text-black"
                      : "text-grayDark"
                  }`}
                >
                  ${(modifier.price / 100).toFixed(2)}
                </span>
              </div>
            </div>
            {!isSingleSelection && isSelected && (
              <span className={`pl-7 mt-2 block`}>
                <QuantityStepper
                  handleQuantityChange={(newQuantity: number) =>
                    handleModifierQuantityChange(
                      group.id,
                      modifier.id,
                      newQuantity
                    )
                  }
                  quantity={modifierQuantity}
                  disablePlus={disablePlus}
                  max={group.maxAllowed}
                  totalQuantity={totalGroupQuantity}
                  modifierId={modifier.id}
                  adaName={modifier?.name}
                />
              </span>
            )}
          </div>
        );
      })}
    </>
  );
};
